import { addDays } from "date-fns";
import { SLA_DAYS, type Suggestion } from "../types/ess";

export interface SlaState {
  due: Date;
  overdue: boolean;
  daysLeft: number;
  breached: boolean;
}

// Categories (from the runtime workflow config, not raw status names —
// see `categoryOf` below) that mean the suggestion will never move again.
// `updated_at` is then treated as a closure-time approximation: the
// backend has no dedicated `closed_at` column, and the last status
// transition into a terminal state is the closest proxy we have.
export const TERMINAL_CATEGORIES = new Set([
  "rejected",
  "closed",
  "completed",
  "done",
]);

/**
 * SLA verdict for a suggestion against the fixed `SLA_DAYS` (90-day)
 * window from submission. Pure function — `categoryOf` is injected so
 * this stays independent of the workflow config fetch (callers pass
 * `(status) => statusCategory(workflowConfig, status)`), and `now` is
 * injectable for deterministic tests.
 *
 *  - `due` = created_at + SLA_DAYS.
 *  - For a terminal-category suggestion, the end-of-life instant used for
 *    the breach check is `updated_at` (closure-time approximation) rather
 *    than the live clock, so a suggestion closed years ago doesn't
 *    "become" breached just because `now` keeps advancing.
 *  - `breached` = the end instant is after `due` — true for either an
 *    open-and-late suggestion or one that was closed/rejected late.
 *  - `overdue` = breached AND still open (excludes terminal statuses) —
 *    the flag that should drive "act now" urgency styling; a suggestion
 *    that already closed late is `breached` but not `overdue`.
 *  - `daysLeft` = ceil((due - now) / day), always measured against `now`
 *    (not the closure instant) — a countdown, so it's only meaningful
 *    while the suggestion is still open.
 */
export function slaState(
  s: Suggestion,
  categoryOf: (status: string) => string,
  now: Date = new Date(),
): SlaState {
  const due = addDays(new Date(s.created_at), SLA_DAYS);
  const terminal = TERMINAL_CATEGORIES.has(categoryOf(s.status));
  const end = terminal ? new Date(s.updated_at) : now;
  const breached = end.getTime() > due.getTime();
  return {
    due,
    breached,
    overdue: !terminal && breached,
    daysLeft: Math.ceil((due.getTime() - now.getTime()) / 86_400_000),
  };
}
